import React, { useState } from 'react';
import { Check, Sparkles, TrendingUp, Calendar } from 'lucide-react';

const MembershipPlans = () => {
  const [selectedDeskSize, setSelectedDeskSize] = useState('standard');

  const plans = [

    {
      title: "XL Desk",
      info: "2ft 8in Wide Desk",
      subtitle: "Standard Individual Desk",
      bgColor: "bg-blue-100",
      pricing: [
        {
          duration: "1 Month",
          allDay: "₹3600",
          morningSession: "₹2400",
          eveningSession: "₹2200"

        },
        {
          duration: "3 Months",
          allDay: "₹10200 @ ₹3400/Month",
          morningSession: "₹6750",
          eveningSession: "₹6450"
        },
        {
          duration: "6 Months",
          allDay: "₹19200 @ ₹3200/Month",
          morningSession: "₹12900",
          eveningSession: "₹12300",
          highlight: true
        }
      ]
    },
    {
      title: "XXL Desk",
      info: "3ft Wide Desk",
      subtitle: "Larger Individual Desk",
      bgColor: "bg-yellow-200",
      pricing: [
        {
          duration: "1 Month",
          allDay: "₹4000",
          morningSession: "₹2600",
          eveningSession: "₹2400"
        },
        {
          duration: "3 Months",
          allDay: "₹11100 @ ₹3700/Month",
          morningSession: "₹7200",
          eveningSession: "₹6900"
        },
        {
          duration: "6 Months",
          allDay: "₹21000 @ ₹3500/Month",
          morningSession: "₹13800",
          eveningSession: "₹13200",
          highlight: true
        }
      ]
    },
    {
      title: "Flexible Pass",
      subtitle: "10 All Day Sessions",
      bgColor: "bg-green-200",
      flexible: true,
      price: "₹1750",
      perDay: "@ ₹175 / Day",
      note: "Come Any 10 Days"
    },
    {
      title: "Trail / Day Session",
      subtitle: "1 All Day Sessions",
      bgColor: "bg-orange-200",
      flexible: true,
      price: "₹200"
    }
  ];

  const features = [
    'All day access',
    'Personal locker with key',
    'High-speed WiFi',
    'Power backup',
    'CCTV surveillance',
    'Clean washrooms',
    'Filtered water',
    'Ergonomic chair & desk'
  ];

  const currentPlans = selectedDeskSize === 'standard' ? plans.standard : plans.premium;

  return (
    <>
      <div className="py-20 bg-gradient-to-b from-blue-50 to-white">

        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Flexible Pricing
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Membership Plans
            </h2>
            <p className="text-lg text-gray-600">
              Choose the plan that works best for your study goals. Save more with longer commitments!
            </p>
          </div>

          <div className='container mx-auto max-w-6xl px-4'>
            {/* Plans Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 mb-12">

              {plans.map((plan, index) => (

                <div
                  key={index}
                  className={`relative bg-white rounded-3xl p-8 border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${plan.bestValue
                      ? 'border-blue-500 shadow-xl'
                      : 'border-gray-200 hover:border-blue-300'
                    }`}
                >
                  {/* Badge */}

                  <div className="text-center mb-6 flex-1">
                    <div className={`relative rounded-3xl p-8 border-2 ${plan.bgColor}`}>
                      <div className="text-center">
                        <div className="inline-flex items-start justify-center gap-1">
                          <h3 className="text-2xl font-bold leading-tight">
                            {plan.title}
                          </h3>


                        </div>

                        <p className="text-sm text-gray-700 font-bold mt-2">
                          {plan.info}
                        </p>
                      </div>
                    </div>

                    {plan.flexible ? (

                      <div className="text-center py-4">

                        <div className="text-4xl font-bold text-gray-900">{plan.price}</div>
                        <p className="text-gray-600 mt-2">{plan.perDay}</p>
                        <p className="font-semibold mt-4">{plan.note}</p>
                      </div>
                    ) : (

                      /* Table */
                      <table className="w-full text-sm text-center border-t">
                        <thead>
                          <tr className="text-gray-600">
                            <th className="py-2">Duration</th>
                            <th className="py-2">All Day</th>
                            <th className="py-2">Morning Session</th>
                            <th className="py-2">Evening Session</th>
                          </tr>
                        </thead>

                        <tbody>
                          {plan.pricing.map((item, i) => (
                            <tr
                              key={i}
                              className={`border-t ${item.highlight ? "bg-green-100 font-semibold" : ""
                                }`}
                            >
                              <td className="py-2">{item.duration}</td>
                              <td className="py-2">{item.allDay}</td>
                              <td className="py-2">{item.morningSession}</td>
                              <td className="py-2">{item.eveningSession}</td>
                            </tr>
                          ))}
                        </tbody>

                      </table>

                    )}
                  </div>

                  <button
                    onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                    className={`mt-auto w-full py-3 rounded-xl font-semibold transition-all duration-300 ${plan.bestValue
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-900 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>

          </div>

          {/* Features List */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-200 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              All Plans Include:
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="bg-blue-100 rounded-full p-1">
                    <Check className="text-blue-600" size={20} />
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="mt-8 text-center text-gray-600 text-sm max-w-2xl mx-auto">
            <p>All Prices are inclusive of GST. To subscribe, provide Aadhaar card, mobile number, and current address.
              Payment accepted via QR code. Desk booking confirmation sent immediately.</p>
            <p>
              ₹500 Security deposit (refundable) will be taken for the locker key.
            </p>
          </div>
        </div>
      </div>


    </>
  );
};

export default MembershipPlans;
